/**
 * Native session-sync wiring for the shared terminal.
 *
 * Binds the protocol-pure sync engine (`./sync`) to the terminal's own
 * `ChatHistoryStore`, so cross-device chat history is a BUILT-IN feature of
 * `<Terminal>` — not a per-host adapter. Auth + storage are already the
 * terminal's job (core owns both), so there is nothing host-specific to
 * inject: every consumer (Paradex, Paradigm, standalone) gets sync by
 * pairing a gateway that serves `/sync/v1/*`.
 *
 * The engine degrades gracefully: every request returns null on failure, so
 * a gateway without `/sync/v1/*` (or a cross-origin gateway without CORS)
 * simply stays local-only — no errors, no config.
 *
 * Scope: sessions index (+ folders + tombstones) and per-session transcripts.
 * UI-state sync (collapsed sidebars / active session) is intentionally out of
 * scope here — it's a cosmetic, host-shaped preference, not history.
 */

import { sanitizeMessageAttachments } from './chat-history-storage';
import type {
  ChatHistoryStore,
  DeletionTombstone,
} from './chat-history-storage';
import {
  emptyIndexDoc,
  mergeIndex,
  mergeMessages,
  syncBaseUrlFromAgentUrl,
  TerminalSyncClient,
} from './sync';
import type { SyncDocRef, SyncIndexDoc, SyncMessagesDoc } from './sync';
import type { ChatMessage, ChatSession, CustomFolder } from './types';

export type TerminalIndexDoc = SyncIndexDoc<ChatSession, CustomFolder>;
export type TerminalMessagesDoc = SyncMessagesDoc<ChatMessage>;
export type TerminalSync = TerminalSyncClient<
  ChatMessage,
  ChatSession,
  CustomFolder
>;

/** Index doc for upload: persisted sessions (temporary chats never sync). */
export function buildIndexDoc(
  store: ChatHistoryStore,
  accountId: string,
  sessions: readonly ChatSession[],
  folders: readonly CustomFolder[],
): TerminalIndexDoc {
  return {
    sessions: sessions.filter((s) => s.isTemporary !== true),
    folders,
    deleted: store.loadTombstones(accountId),
  };
}

function asIndexDoc(value: unknown): TerminalIndexDoc {
  const v = (value ?? {}) as Partial<TerminalIndexDoc>;
  return {
    sessions: Array.isArray(v.sessions) ? v.sessions : [],
    folders: Array.isArray(v.folders) ? v.folders : [],
    deleted: Array.isArray(v.deleted) ? v.deleted : [],
  };
}

function asMessagesDoc(value: unknown): TerminalMessagesDoc {
  const v = (value ?? {}) as Partial<TerminalMessagesDoc>;
  if (!Array.isArray(v.messages)) return { messages: [] };
  // Remote docs are otherwise adopted without per-message validation, and
  // attachment previewUrls end up in <img src> — coerce them at this
  // boundary, same as parseMessages does for localStorage.
  return {
    messages: v.messages.map((m) =>
      m != null && typeof m === 'object' ? sanitizeMessageAttachments(m) : m,
    ),
  };
}

function resolveTerminalConflict(
  ref: SyncDocRef,
  local: unknown,
  remote: unknown,
): unknown {
  if (ref.kind === 'index') {
    return mergeIndex(asIndexDoc(local), asIndexDoc(remote));
  }
  if (ref.kind === 'session') {
    return {
      messages: mergeMessages(
        asMessagesDoc(local).messages,
        asMessagesDoc(remote).messages,
      ),
    };
  }
  return local;
}

function persistMergedDoc(
  store: ChatHistoryStore,
  accountId: string,
  ref: SyncDocRef,
  merged: unknown,
): void {
  if (ref.kind === 'index') {
    const doc = asIndexDoc(merged);
    store.saveSessions(accountId, doc.sessions);
    store.saveCustomFolders(accountId, doc.folders);
    store.saveTombstones(
      accountId,
      doc.deleted as readonly DeletionTombstone[],
    );
    return;
  }
  if (ref.kind === 'session') {
    store.saveSessionMessages(
      accountId,
      ref.sessionId,
      asMessagesDoc(merged).messages,
    );
  }
}

/**
 * Create a sync client for this account, or null when there is no usable
 * gateway URL yet. The caller owns the lifecycle (`dispose()` on account
 * switch / unmount).
 */
export function createSessionSync(params: {
  readonly store: ChatHistoryStore;
  readonly accountId: string;
  readonly agentUrl: string | null;
  /**
   * Explicit sync base URL (no trailing slash) — e.g. a backend forward that
   * injects the gateway credential server-side. When omitted, the base is
   * derived from the gateway ws URL.
   */
  readonly baseUrl?: string;
  /**
   * Auth headers for every sync request, or null when no credential is
   * available (treated as transient — the client keeps retrying).
   */
  readonly getAuthHeaders: () => Readonly<Record<string, string>> | null;
}): TerminalSync | null {
  const baseUrl = params.baseUrl ?? syncBaseUrlFromAgentUrl(params.agentUrl);
  if (baseUrl == null) return null;
  return new TerminalSyncClient<ChatMessage, ChatSession, CustomFolder>({
    baseUrl,
    getAuthHeaders: params.getAuthHeaders,
    resolveConflict: resolveTerminalConflict,
    onMerged: (ref, merged) =>
      persistMergedDoc(params.store, params.accountId, ref, merged),
  });
}

/** One transcript pulled lazily (e.g. opening a session created elsewhere). */
export async function fetchAndMergeSessionFromRemote(
  store: ChatHistoryStore,
  accountId: string,
  client: TerminalSync,
  sessionId: string,
): Promise<readonly ChatMessage[] | null> {
  const envelope = await client.fetchSession(sessionId);
  if (envelope == null) return null;
  // Honor the session's `clearedAt` tombstone, like `hydrateFromRemote`: a
  // cleared chat's old transcript intentionally stays on the gateway (the
  // empty-PUT guard never uploads `[]`), and this pull fires exactly when the
  // local blob is empty — the post-clear state — so an unfiltered merge would
  // resurrect the cleared messages (e.g. clear → switch away → switch back).
  const clearedAt =
    store.loadSessions(accountId).find((s) => s.sessionId === sessionId)
      ?.clearedAt ?? 0;
  const remote = asMessagesDoc(envelope.doc).messages.filter(
    (m) => m.timestamp > clearedAt,
  );
  if (remote.length === 0) return null;
  const merged = mergeMessages(
    store
      .loadSessionMessages(accountId, sessionId)
      .filter((m) => m.timestamp > clearedAt),
    remote,
  );
  store.saveSessionMessages(accountId, sessionId, merged);
  return merged;
}

export interface RemoteHydrationResult {
  /** Merged sessions index (already persisted to the store). */
  readonly sessions: readonly ChatSession[];
  /** Merged custom folders (already persisted). */
  readonly folders: readonly CustomFolder[];
  /** Session ids whose transcripts changed locally during the merge. */
  readonly updatedSessionIds: ReadonlySet<string>;
}

/**
 * Merge the server snapshot into local storage and queue uploads for anything
 * that exists only locally (including the one-shot migration of pre-sync
 * history). Returns null when the gateway is unreachable — the caller
 * continues local-only and the UI never blocks.
 *
 * Data-safety invariants: merges are unions (neither side dropped); tombstones
 * remove transcripts on both sides; local-only history is uploaded, not
 * replaced.
 */
export async function hydrateFromRemote(
  store: ChatHistoryStore,
  accountId: string,
  client: TerminalSync,
): Promise<RemoteHydrationResult | null> {
  const snapshot = await client.fetchSnapshot();
  if (snapshot == null) return null;

  const localIndex = buildIndexDoc(
    store,
    accountId,
    store.loadSessions(accountId),
    store.loadCustomFolders(accountId),
  );
  const remoteIndex =
    snapshot.index == null
      ? emptyIndexDoc<ChatSession, CustomFolder>()
      : asIndexDoc(snapshot.index.doc);
  const merged = mergeIndex(localIndex, remoteIndex);
  store.saveSessions(accountId, merged.sessions);
  store.saveCustomFolders(accountId, merged.folders);
  store.saveTombstones(
    accountId,
    merged.deleted as readonly DeletionTombstone[],
  );

  const liveIds = new Set(merged.sessions.map((s) => s.sessionId));
  const remoteIds = new Set(snapshot.sessionList.map((item) => item.id));
  const clearedAtById = new Map(
    merged.sessions.map((s) => [s.sessionId, s.clearedAt ?? 0] as const),
  );
  const updatedSessionIds = new Set<string>();

  // Merge snapshot transcripts into local blobs. Drop messages older than the
  // session's `clearedAt` (the user explicitly wiped them on some device).
  for (const [sessionId, envelope] of Object.entries(snapshot.sessions)) {
    if (!liveIds.has(sessionId)) continue; // tombstoned — skip
    const clearedAt = clearedAtById.get(sessionId) ?? 0;
    const raw = store.loadSessionMessages(accountId, sessionId);
    const local = raw.filter((m) => m.timestamp > clearedAt);
    const remote = asMessagesDoc(envelope.doc).messages.filter(
      (m) => m.timestamp > clearedAt,
    );
    const mergedMessages = mergeMessages(local, remote);
    // Flag the session whenever the persisted blob differs from what was on
    // disk BEFORE the filter: either the merge pulled in remote messages, or
    // a remote `clearedAt` dropped local ones. Comparing against the filtered
    // `local` alone misses the second case — a remotely-cleared open session
    // would never be flagged, the provider would keep its stale in-memory
    // transcript, and the persist effect would write (and re-upload) the
    // cleared messages right back over the just-wiped blob. (`merged ⊇ local`
    // as a set, so length checks here are exact, not heuristic.)
    if (mergedMessages.length !== local.length || local.length !== raw.length) {
      updatedSessionIds.add(sessionId);
    }
    store.saveSessionMessages(accountId, sessionId, mergedMessages);
  }

  // A remote clear must also reach sessions whose transcript was NOT bundled
  // in the snapshot (only the most-recent transcripts are): their merged
  // index entry can still carry a fresher `clearedAt`, and without this pass
  // the stale local blob would survive — and be re-uploaded by the local-only
  // migration below or the provider's persist effect.
  const bundledIds = new Set(Object.keys(snapshot.sessions));
  for (const s of merged.sessions) {
    const clearedAt = s.clearedAt ?? 0;
    if (clearedAt === 0 || bundledIds.has(s.sessionId)) continue;
    const raw = store.loadSessionMessages(accountId, s.sessionId);
    const filtered = raw.filter((m) => m.timestamp > clearedAt);
    if (filtered.length !== raw.length) {
      store.saveSessionMessages(accountId, s.sessionId, filtered);
      updatedSessionIds.add(s.sessionId);
    }
  }

  // Apply tombstones locally and propagate session deletions to the server
  // (skip the DELETE when the server doesn't have the doc).
  for (const tombstone of merged.deleted) {
    if (tombstone.kind !== 'session') continue;
    if (liveIds.has(tombstone.id)) continue; // resurrected by later activity
    store.deleteSessionMessages(accountId, tombstone.id);
    if (remoteIds.has(tombstone.id)) {
      client.queueDeleteSession(tombstone.id);
    }
  }

  // Upload local-only transcripts (also the first-run migration of pre-sync
  // history against an empty server).
  for (const session of merged.sessions) {
    if (remoteIds.has(session.sessionId)) continue;
    const messages = store.loadSessionMessages(accountId, session.sessionId);
    if (messages.length === 0) continue;
    client.queueSession(session.sessionId, { messages });
  }

  // Converge the index.
  client.queueIndex(
    buildIndexDoc(store, accountId, merged.sessions, merged.folders),
  );

  return {
    sessions: merged.sessions,
    folders: merged.folders,
    updatedSessionIds,
  };
}
